import { useState, useMemo, useEffect } from 'react';
import {
  useQuery,
} from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod";
import * as zod from "zod";
import * as zVal from '../lib/validations';
import { getMemberResourcesInitialData, postMemberResourcesListData } from '../lib/api';
import { captureError } from '../lib/sentry';
import '../App.css';
import { FileText, Download } from 'lucide-react';
import Text from '../components/ui/text';
import Select from '../components/ui/select';
import Button from '../components/ui/button';
import Spinner from '../components/ui/spinner';
import PdfIconSrc from '../assets/images/pdf-icon.svg';
import DownloadIconSrc from '../assets/images/download-icon.svg';

const formSchema = zod
  .object({
    year: zVal.selectWithNumberValueRequired(),
    county: zVal.selectWithNumberValueRequired(),
    language: zVal.selectWithNumberValueRequired(),
    plan: zVal.selectWithNumberValueRequired(),
  });
const resolver = zodResolver(formSchema);

function App() {
  const [requestParams, setRequestParams] = useState(null);
  const [planSelectionAttempt, setPlanSelectionAttempt] = useState(false);

  // Form Validation
  const {
    resetField,
    setValue,
    getValues,
    register,
    trigger,
    watch,
    formState: { errors, isValidating, isValid }
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {},
    resolver
  });

  // Request Initial Data
  const { isPending: initialDataIsPending, isError: initialDataIsError, data: initialData, error: initialDataError } = useQuery({
    queryKey: ['memberResourcesInitialData'],
    queryFn: async () => {
      try {
        const response = await getMemberResourcesInitialData();
        return response;
      } catch (error) {
        // Capturar el error en Sentry
        captureError(error, { component: 'member-resources', query: 'memberResourcesInitialData' });
        throw error; // Relanzar el error para que React Query lo maneje
      }
    },
  });

  // Request List Data
  const { isPending: listDataIsPending, isError: listDataIsError, data: listData, error: listDataError  } = useQuery({
    queryKey: ['memberResourcesListData', requestParams],
    queryFn: async () => {
      try {
        const response = await postMemberResourcesListData(requestParams);
        return response;
      } catch (error) {
        // Capturar el error en Sentry
        captureError(error, { component: 'member-resources', query: 'memberResourcesListData' });
        throw error; // Relanzar el error para que React Query lo maneje
      }
    },
    enabled: !!requestParams,
  });

  const watchYear = watch('year');
  const watchCounty = watch('county');
  const watchLanguage = watch('language');
  const watchPlan = watch('plan');
  
  useEffect(() => {
    if (planSelectionAttempt) {
      trigger(["year", "county","language"]);
    }
  }, [planSelectionAttempt]);

  useEffect(() => { 
    resetField("plan");
  }, [watchYear, watchCounty]);

  useEffect(() => {
    const { year, language, plan } = getValues();

    if (isValid) {
      setRequestParams({
        "PlanId": plan,
        "VersionId": parseInt(year),
        "LanguageId": parseInt(language)
      }); 
    } else {
      setRequestParams(null);
    }

  }, [isValid, watchLanguage, watchPlan]);

  // Select Lists
  const yearList = useMemo(() => initialData?.data?.yearList.map(i => ({ label: i.name, value: i.id})), [initialData]);
  const countyList = useMemo(() => initialData?.data?.countyList.filter(p => p.yearId === parseInt(watchYear)).map(i => ({ label: i.name, value: i.id})), [initialData, watchYear]);
  const languageList = useMemo(() => initialData?.data?.languageList.map(i => ({ label: i.name, value: i.id})), [initialData]);
  const planList = useMemo(() => initialData?.data?.planList.filter(p => parseInt(p.yearId) === parseInt(watchYear) && parseInt(p.countyId) === parseInt(watchCounty)).map(i => ({ label: i.name, value: i.id})), [initialData, watchYear, watchCounty]);

  return (
    <div className='component-container font-monserrat w-full h-auto flex flex-col justify-start items-start font-normal'>
      <div className='w-full h-auto flex flex-col gap-10'>
        <div><Text type='h5'>PLAN DOCUMENTS</Text></div>
        <div className='flex flex-col gap-5 pb-12 border-b border-gray-600'>
          <div className='flex flex-col sm:flex-row gap-5'>
            <div className='flex-1 flex flex-col justify-start items-start'>
              <Text type='label' htmlFor='year'>Year</Text>
              <Select
                {...register('year')}
                size='sm'
                label={<Text>Select a year</Text>} 
                options={yearList}
                selectionMode="single"
                disallowEmptySelection={true}
                isRequired={true}
                errorMessage={errors.year?.message || null}
                isInvalid={errors.year ? true : false}
              />
            </div>
            <div className='flex-1 flex flex-col justify-start items-start'>
              <Text type='label' htmlFor='county'>County</Text>
              <Select
                {...register('county')}
                size='sm'
                label={<Text>Select a county</Text>}
                options={countyList}
                selectionMode="single"
                disallowEmptySelection={true}
                isRequired={true}
                errorMessage={errors.county?.message || null}
                isInvalid={errors.county ? true : false}
              />
            </div>
            <div className='flex-1 flex flex-col justify-start items-start'>
              <Text type='label' htmlFor='language'>Language</Text>
              <Select
                {...register('language')}
                size='sm'
                label={<Text>Select a language</Text>}
                options={languageList}
                selectionMode="single"
                disallowEmptySelection={true}
                isRequired={true} 
                errorMessage={errors.language?.message || null}
                isInvalid={errors.language ? true : false}
              />
            </div>
          </div>
          <div className='flex flex-row'>
            <div className='flex-1 flex flex-col justify-start items-start'>
              <Text type='label' htmlFor='plan'>Solis Plan Name</Text>
              <Select
                {...register('plan')}
                size='sm'
                label={<Text>Select a plan</Text>}
                options={planList}
                selectionMode="single"
                disallowEmptySelection={true}
                isRequired={true}
                errorMessage={errors.plan?.message || null}
                isInvalid={errors.plan ? true : false}
                onClick={() => setPlanSelectionAttempt(true)}
              />
            </div>
          </div>
        </div>
        {requestParams && listDataIsPending && <div className='w-full p-10 flex flex-row justify-center items-center'><Spinner /></div>}
        {listData?.data?.planDocumentList && (<div className='flex flex-col'>
          <div className='text-start'><Text type='label'>Plan documents</Text></div>
          <div className='grid grid-cols-1 sm:grid-cols-4 gap-5 pt-3'>
            {listData?.data?.planDocumentList.length === 0 ? (
              <div className='col-span-1 sm:col-span-4 min-h-40 flex flex-row justify-center items-center'><Text>No results found.</Text></div>
            ) : (
              <>
                {listData.data.planDocumentList.map(item => {
                  return (
                    <div key={item.id} className='flex flex-row justify-between sm:flex-col gap-3 border-b border-gray-400 pb-3'>
                      <div className='flex flex-row items-start gap-5 text-gray-600'>
                        <div>
                          <img src={PdfIconSrc} alt='Download PDF' className='w-8 h-auto'/>
                        </div>
                        <div className='flex flex-row justify-between items-center sm:flex-col sm:items-start gap-5'>
                          <div className='text-lg font-semibold text-gray-700'>
                            {item.name}
                          </div>
                          <div>
                            <a href={item.url} target='_blank'>
                              <Button size='sm' radius='full' className='bg-gray-100 text-gray-600 text-base w-10 p-0 m-0 sm:w-full sm:min-w-36'>
                                <Download size={18} />
                                <Text className='hidden sm:block'>Download</Text>
                              </Button>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>)}
      </div>
    </div>
  )
}

export default App
